import { useEffect, useRef, type RefObject } from "react";

/**
 * Scroll lock, Escape-to-close, and initial focus for modal/drawer overlays.
 *
 * Focus the panel once when `open` becomes true. Do not re-focus when `busy`
 * or `onClose` identity changes (Setup drawers pass inline onClose closures,
 * which would steal focus from inputs mid-typing).
 */
export function useOverlayShell(open: boolean, busy: boolean, onClose: () => void): {
  panelRef: RefObject<HTMLDivElement | null>;
} {
  const panelRef = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  const busyRef = useRef(busy);
  onCloseRef.current = onClose;
  busyRef.current = busy;

  useEffect(() => {
    if (!open) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    function onKeyDown(event: KeyboardEvent): void {
      if (event.key !== "Escape" || busyRef.current) return;
      event.preventDefault();
      onCloseRef.current();
    }

    document.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const panel = panelRef.current;
    if (!panel) return;
    if (panel.contains(document.activeElement)) return;
    panel.focus();
  }, [open]);

  return { panelRef };
}
